import { formatCurrency } from 'common/FormatUtilities';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { MealPlanFoodItemUpsellFragment, MealPlanFoodType, UpsellTier } from 'src/gqlReactTypings.generated.d';
import Chip, { ChipColor } from 'web-common/src/shared/design-system/components/Chip';
import { Icon, IconName } from 'web-common/src/shared/design-system/components/Icon';

const ORGANIC_TIERS = new Set<UpsellTier>([
  UpsellTier.OrganicVegetarian,
  UpsellTier.OrganicProtein,
  UpsellTier.FullOrganic,
  UpsellTier.GrassFed,
  UpsellTier.WildCaught,
]);

const LEGACY_UPSELL_TIERS = new Set<UpsellTier>([UpsellTier.Premium, UpsellTier.PremiumPlus]);
const TIER_RESTRICTED_FOOD_TYPES = new Set<MealPlanFoodType>([
  MealPlanFoodType.Side,
  MealPlanFoodType.Staple,
  MealPlanFoodType.Dessert,
  MealPlanFoodType.DessertMulti,
]);

export const isUpsellTierEligibleForFoodType = (
  tier: UpsellTier,
  foodType: MealPlanFoodType | null | undefined
): boolean => (foodType && TIER_RESTRICTED_FOOD_TYPES.has(foodType) ? !LEGACY_UPSELL_TIERS.has(tier) : true);

interface UpsellPriceChipProps {
  upsell: MealPlanFoodItemUpsellFragment | null | undefined;
  servingsPerMeal: number;
}

export const UpsellPriceChip: React.FC<UpsellPriceChipProps> = ({ upsell, servingsPerMeal }) => {
  const upsellsEnabled = useFeatureFlag('upsellsEnabled');

  if (!upsell || !upsellsEnabled) {
    return null;
  }

  const priceMultiplier = servingsPerMeal || 1;
  const priceCopy = `+${formatCurrency(upsell.price * priceMultiplier, true)}`;
  const color = ORGANIC_TIERS.has(upsell.tier) ? ChipColor.MINT_50 : ChipColor.ELDERBERRY_25;
  return <Chip color={color} copy={priceCopy} />;
};

const ORGANIC_TIER_ICON_NAMES: Partial<Record<UpsellTier, IconName>> = {
  [UpsellTier.GrassFed]: 'grassFed',
  [UpsellTier.WildCaught]: 'wildCaught',
};

const getUpsellChipConfig = (tier: UpsellTier): { iconElement: React.ReactNode; color: ChipColor } => {
  if (ORGANIC_TIERS.has(tier)) {
    const iconName = ORGANIC_TIER_ICON_NAMES[tier] ?? 'organic';
    return {
      iconElement: <Icon name={iconName} size={16} />,
      color: ChipColor.MINT_50,
    };
  }

  if (tier === UpsellTier.PremiumProtein) {
    return {
      iconElement: <Icon name='premium' size={16} />,
      color: ChipColor.ELDERBERRY_25,
    };
  }

  return {
    iconElement: <Icon name='premium' size={16} />,
    color: ChipColor.ELDERBERRY_25,
  };
};

interface UpsellLabelChipProps {
  upsell: MealPlanFoodItemUpsellFragment | null | undefined;
  applyFullHeight?: boolean;
}

export const UpsellLabelChip: React.FC<UpsellLabelChipProps> = ({ upsell, applyFullHeight }) => {
  const upsellsEnabled = useFeatureFlag('upsellsEnabled');

  if (!upsell || !upsellsEnabled) {
    return null;
  }

  const { iconElement, color } = getUpsellChipConfig(upsell.tier);
  const copy = upsell.displayName || (upsell.tier === UpsellTier.PremiumPlus ? 'Premium Plus' : 'Premium');

  return <Chip applyFullHeight={applyFullHeight} color={color} copy={copy} icon={iconElement} />;
};
